import * as z from "zod";
import { codeName, pagination } from "../index";

// What waits for an approval (B-60, ADR-224): every gate instance still open, in any domain, with what it waits on,
// who decides, since when and when the next notice of its escalation leaves (ADR-210). Served by reference, which
// administers the gates; the domains open and decide them (ADR-042).

/** A gate waits `pending`, or `escalated` once the escalation role was told: either still waits for its decision. */
export const waitingGateStatuses = ["pending", "escalated"] as const;
export type WaitingGateStatus = (typeof waitingGateStatuses)[number];

/** The notices of the escalation (ADR-210): who decides is reminded, then the escalation role is told. */
export const gateNoticeKinds = ["reminder", "escalation"] as const;

export const waitingGateDto = z
  .object({
    /** The instance of the gate. */
    id: z.uuid(),
    gate: codeName,
    entity: z.object({
      /** Schema-qualified, as the gate guards it: `procurement.purchase_order`. */
      type: z.string(),
      id: z.uuid(),
      /** How people name it: a PO number, the code of a movement, the user of a reset; null for one not known. */
      label: z.string().nullable(),
      /** The entity whose page shows this one, for one with no page of its own: the user of a reset of the factors. */
      subjectId: z.uuid().nullable(),
    }),
    status: z.enum(waitingGateStatuses),
    requestedAt: z.iso.datetime(),
    /** First and last name of who asked; null for a request of the system. */
    requestedBy: z.string().nullable(),
    /** The working days since the day of the request (B-77): Saturday, Sunday and the holidays do not count. */
    workingDaysWaited: z.number().int(),
    /** Who decides. */
    approverRole: codeName,
    /** Who hears of it second, as the gate says now; null, nobody. Told already once `status` is `escalated`. */
    escalationRole: codeName.nullable(),
    escalatedAt: z.iso.datetime().nullable(),
    /**
     * The next notice and the working day it leaves on, as the daily run counts it; a day already past leaves at the
     * next run. Null when none is left: escalated, a gate off, or one that reminds and escalates to nobody.
     */
    nextNotice: z.object({ kind: z.enum(gateNoticeKinds), on: z.iso.date() }).nullable(),
  })
  .meta({ id: "WaitingGate" });
export type WaitingGate = z.output<typeof waitingGateDto>;

const waitingGateFilters = { status: z.enum(waitingGateStatuses).optional() };

/** The gates that wait, the oldest request first. */
export const listWaitingGatesInput = z.object({
  ...waitingGateFilters,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListWaitingGatesInput = z.input<typeof listWaitingGatesInput>;

/** How many gates wait with the same filters, and since when the oldest request (ADR-086): the queue of the home. */
export const countWaitingGatesInput = z.object(waitingGateFilters);
export type CountWaitingGatesInput = z.input<typeof countWaitingGatesInput>;
